import { useReveal } from "@/hooks/useReveal";
import { useLenis } from "@/components/SmoothScrollProvider";

// Plain, factual Q&A in the owner's voice. Kept in the DOM as visible text so
// both readers and AI crawlers can lift a self-contained answer. Mirrors the
// FAQPage JSON-LD in public/index.html, question for question.
export const FAQS = [
  {
    q: "How do you sell your fabric?",
    a: "By the roll or by the pound. For bigger orders we ship container loads. Tell us what you need and we'll quote it.",
  },
  {
    q: "Do you work with international buyers?",
    a: "Yes. We're a domestic supplier first, American-made goods for the home market, but we work with international buyers every day. Call or WhatsApp us and we'll sort out the details.",
  },
  {
    q: "What kinds of fabric do you carry?",
    a: "Spandex and specialty stretch of every kind. Nylon, polyester, cotton, and satin spandex, prints, meshes, laces, powernet, athletic and performance goods, plus pound goods sold by the pound. If it stretches, chances are it's on our floor.",
  },
  {
    q: "Can I see the fabric before I buy?",
    a: "Yes. Take the 360° warehouse tour on this site and look around the aisles like you're standing in them. For a closer look or samples, give us a call.",
  },
  {
    q: "How do I get pricing?",
    a: "Call or WhatsApp +1 718 781 4444, or email magnummoez@aol.com. Tell us the fabric and the quantity, and we'll give you a price.",
  },
  {
    q: "Who is Spandex American Fabrics?",
    a: "We're Cherechian Trading Co, the same family that's traded fabric since 1967. Two generations on, we're still family-owned and still run from the warehouse floor. Online we go by Spandex American Fabrics.",
  },
];

const FaqItem = ({ item, index }) => {
  const [ref, shown] = useReveal({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      data-testid="faq-item"
      className={`border-t border-dashed border-kraft py-7 transition-[opacity,transform] duration-[600ms] ease-out ${
        shown ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
      }`}
      style={{ transitionDelay: shown ? `${index * 100}ms` : "0ms" }}
    >
      <h3 className="font-display text-xl font-bold text-warmwhite md:text-2xl">
        {item.q}
      </h3>
      <p className="mt-3 max-w-2xl font-inter text-base leading-[1.7] text-warmwhite/75">
        {item.a}
      </p>
    </div>
  );
};

export const FAQSection = () => {
  const lenis = useLenis();
  const [headRef, headShown] = useReveal({ threshold: 0.2 });

  const goToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="faq" data-testid="section-faq" className="seam-top bg-ink">
      <div
        ref={headRef}
        className={`mx-auto max-w-[900px] px-5 py-[120px] transition-[opacity,transform] duration-[600ms] ease-out md:px-7 ${
          headShown ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
        }`}
      >
        <p className="eyebrow text-xs text-kraft">FAQ</p>
        <h2 className="font-display mt-5 text-3xl font-extrabold leading-[1.08] text-warmwhite md:text-[40px]">
          Questions we hear a lot.
        </h2>

        <div className="mt-12 flex flex-col">
          {FAQS.map((item, i) => (
            <FaqItem key={item.q} item={item} index={i} />
          ))}
        </div>

        <a
          href="#contact"
          onClick={goToContact}
          data-testid="faq-contact-link"
          className="eyebrow mt-10 inline-block text-[13px] text-warmwhite transition-colors duration-300 ease-out hover:text-kraft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft"
        >
          Still have a question? Call us →
        </a>
      </div>
    </section>
  );
};
